import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import "./TrailDetail.css";

export default function TrailDetail() {
  const { id } = useParams();                 // may be WRONG (trail name)
  const navigate = useNavigate();

  const [trail, setTrail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [firstClueCaseFiles, setFirstClueCaseFiles] = useState([]);

  const [popupImage, setPopupImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  // Detect logged-in user
  useEffect(() => {
    const auth = getAuth();
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
    });
  }, []);

  // ============================================================
  // LOAD TRAIL (AND AUTO-FIX WRONG URL ID)
  // ============================================================
  useEffect(() => {
    async function loadTrail() {
      try {
        // Attempt 1: Load directly using doc ID
        const directRef = doc(db, "trails", id);
        const directSnap = await getDoc(directRef);

        if (directSnap.exists()) {
          setTrail({ ...directSnap.data(), realId: directSnap.id });
          setLoading(false);
          return;
        }

        // Attempt 2: ID was actually a NAME — find trail by name
        const q = query(collection(db, "trails"), where("name", "==", id));
        const snap2 = await getDocs(q);

        if (!snap2.empty) {
          const docFound = snap2.docs[0];

          // Redirect to correct URL
          navigate(`/trails/${docFound.id}`, { replace: true });
          return;
        }

        // Nothing found
        setTrail(null);
      } catch (err) {
        console.error("Load error:", err);
      }

      setLoading(false);
    }

    loadTrail();
  }, [id, navigate]);

  // ============================================================
  // FETCH FIRST CLUE'S CASE FILES
  // ============================================================
  useEffect(() => {
    async function loadFirstClueCaseFiles() {
      if (!trail?.realId) return;

      try {
        // Query for the first clue (lowest order number)
        const q = query(
          collection(db, "clues"),
          where("trailId", "==", trail.realId),
          orderBy("order", "asc"),
          limit(1)
        );

        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const firstClue = snapshot.docs[0].data();
          setFirstClueCaseFiles(firstClue.caseFiles || []);
        } else {
          setFirstClueCaseFiles([]);
        }
      } catch (err) {
        console.error("Error loading first clue case files:", err);
        setFirstClueCaseFiles([]);
      }
    }

    loadFirstClueCaseFiles();
  }, [trail]);

  if (loading) return <div className="trail-detail">Loading…</div>;
  if (!trail) return <div className="trail-detail">Trail not found.</div>;

  // Normalize case files from first clue
  const safeCaseFiles = (firstClueCaseFiles || []).map((file) => {
    if (typeof file === "string") {
      return { full: file, thumb: file };
    }
    return {
      full: file.full || file.thumb || "",
      thumb: file.thumb || file.full || "",
    };
  });

  // Google Maps embed
  const getGoogleEmbedURL = () => {
    if (!trail.mapURL) return "";

    if (trail.mapURL.includes("google.com")) return trail.mapURL;

    return `https://www.google.com/maps?q=${trail.mapURL}&z=15&output=embed`;
  };

  const openMapInApp = () => {
    if (!trail.mapURL) return;

    if (trail.mapURL.includes("google.com")) {
      window.open(trail.mapURL, "_blank");
    } else {
      const url = `https://www.google.com/maps/search/?api=1&query=${trail.mapURL}`;
      window.open(url, "_blank");
    }
  };

  const toggleModal = () => setShowModal(!showModal);

  // ============================================================
  // Start Trail — ALWAYS use realId (Firestore doc ID)
  // ============================================================
  const startTrial = () => {
    if (!user) {
      alert("You need to be logged in to start the trail!");
      navigate("/login");
      return;
    }

    navigate(`/start-trail/${trail.realId}`);
  };

  return (
    <div className="trail-detail">
      {trail.bannerImageURL && (
        <img className="trail-banner" src={trail.bannerImageURL} alt="" />
      )}

      <div className="trail-detail-content">
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
          <h1 className="trail-title" style={{ margin: 0 }}>{trail.name}</h1>
          <button
            onClick={() => setShowWarning(true)}
            style={{
              background: "none",
              border: "2px solid #f6c947",
              borderRadius: "50%",
              width: "30px",
              height: "30px",
              cursor: "pointer",
              fontSize: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#f6c947",
              fontWeight: "bold"
            }}
            title="Important information"
          >
            ?
          </button>
        </div>
        
        <p className="trail-city">{trail.city}</p>

        <p className="trail-description">{trail.description}</p>

        {trail.isPaid === false && (
          <p className="trail-price free">This Trail is Free</p>
        )}

        <button onClick={startTrial} className="start-trail-btn">
          {trail.isPaid === false ? "Start This Free Trail" : "Start Paid Trail"}
        </button>

        {/* CASE FILES - From first clue only */}
        <h2 className="case-title">Case Files</h2>

        {safeCaseFiles.length === 0 && (
          <p className="case-empty">No case files available.</p>
        )}

        <div className="case-file-grid">
          {safeCaseFiles.map((img, i) => (
            <img
              key={i}
              src={img.thumb}
              alt={`Case file ${i + 1}`}
              className="case-file-thumb"
              onClick={() => setPopupImage(img.full)}
              style={{ cursor: "pointer" }}
            />
          ))}
        </div>

        {popupImage && (
          <div className="popup-overlay" onClick={() => setPopupImage(null)}>
            <img src={popupImage} className="popup-image" alt="" />
          </div>
        )}

        {/* MAP - Now below case files */}
        <div className="trail-map-box">
          {trail.mapType === "google" && trail.mapURL && (
            <>
              <iframe
                src={getGoogleEmbedURL()}
                title="Map"
                className="trail-map-iframe"
              />
              <button onClick={openMapInApp} className="open-map-button">
                Open in Map App
              </button>
              <p>
                <a href="#!" onClick={toggleModal} className="disclaimer-link">
                  Read Disclaimer about using Maps
                </a>
              </p>
            </>
          )}

          {trail.mapType === "image" && trail.mapURL && (
            <img src={trail.mapURL} alt="" className="trail-map-image" />
          )}
        </div>
      </div>

      {/* Warning Modal */}
      {showWarning && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 style={{ color: "#f6c947" }}>⚠️ Important Information</h2>
            <p>
              Please note that some clues may be obstructed depending on the time of year you're doing the trail, due to events being held in the area.
            </p>
            <p style={{ marginTop: "10px" }}>
              If you encounter any difficulties locating a clue, please use the hint button or contact support.
            </p>
            <button onClick={() => setShowWarning(false)} className="close-modal-btn">
              Got it!
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Maps Usage Disclaimer</h2>
            <p>
              Always follow local laws and travel safely. Map data may be
              inaccurate or outdated.
            </p>
            <button onClick={toggleModal} className="close-modal-btn">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}